import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from './ui/utils';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface AddActionItemDialogProps {
  open: boolean;
  onClose: () => void;
  companyId?: string; // Optional now
  companyName?: string;
  companies?: { companyId: string; companyName: string }[]; // List of available companies
  accessToken: string;
  currentUser: any;
  onSuccess?: () => void;
}

export function AddActionItemDialog({
  open,
  onClose,
  companyId: initialCompanyId,
  companyName,
  companies = [],
  accessToken,
  currentUser,
  onSuccess
}: AddActionItemDialogProps) {
  const [loading, setLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId || '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [waitingOn, setWaitingOn] = useState<'client' | 'consultant' | 'internal'>('consultant');
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [users, setUsers] = useState<{ id: string; name: string; role: string }[]>([]);

  useEffect(() => {
    if (open) {
      if (initialCompanyId) {
        setSelectedCompanyId(initialCompanyId);
      } else if (companies.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(companies[0].companyId);
      }
    }
  }, [open, initialCompanyId, companies]);

  useEffect(() => {
    if (open && selectedCompanyId) {
      loadUsers(selectedCompanyId);
    }
  }, [open, selectedCompanyId]);

  const loadUsers = async (cId: string) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company/${cId}/users`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        
        // Default to current user if they are in the list
        if (!assignedTo && data.users.some((u: any) => u.id === currentUser.id)) {
          setAssignedTo(currentUser.id);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCompanyId) {
      toast.error('Please select a client');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter a title for the action item');
      return;
    }

    try {
      setLoading(true);

      // 1. Create the Config Item (Action Item)
      const createResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${selectedCompanyId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name,
            description,
            category: 'action_item', // Use a generic category or dedicated one
            phase: 1, // Default phase
            userId: currentUser.id,
            userName: currentUser.name || currentUser.email,
          }),
        }
      );

      if (!createResponse.ok) {
        throw new Error('Failed to create action item');
      }

      const { item } = await createResponse.json();

      // 2. Assign it and set metadata
      const assignResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${selectedCompanyId}/${item.id}/assign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: assignedTo,
            userName: users.find(u => u.id === assignedTo)?.name || 'Unknown',
            assignedBy: currentUser.id,
            dueDate: dueDate ? dueDate.toISOString() : null,
            waitingOnRole: waitingOn, // New field
            assignmentNote: description,
          }),
        }
      );

      if (!assignResponse.ok) {
        throw new Error('Failed to assign action item');
      }

      toast.success('Action item created successfully');
      onSuccess?.();
      onClose();
      
      // Reset form
      setName('');
      setDescription('');
      setDueDate(undefined);
      setWaitingOn('consultant');
    } catch (error) {
      console.error('Error creating action item:', error);
      toast.error('Failed to create action item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Action Item</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {!initialCompanyId && companies.length > 0 && (
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.companyId} value={company.companyId}>
                      {company.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Title</Label>
            <Input 
              id="name" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="e.g., Review Quarterly Report"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea 
              id="description" 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Add details about this task..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Assigned To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger>
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dueDate ? format(dueDate, "PPP") : <span>No Due Date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dueDate}
                    onSelect={setDueDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Waiting On</Label>
            <Select 
              value={waitingOn} 
              onValueChange={(val: any) => setWaitingOn(val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consultant">Consultant</SelectItem>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Create Action Item
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
